import React from "react";
import { Button, Input } from "@pd/pd-ui-kit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import {
  getAuditStatusPopoverItems,
  getStatusPopoverItems,
} from "~/pages/CatalogApplications/model/candidateStatusUi";
import { getCandidateTypeOptions } from "~/pages/CatalogApplications/model/candidateTypeLabels";
import { Select } from "~/shared/ui/Select";

import s from "./CatalogFilters.less";

type SelectValue = { value: string; label: string } | null;

export interface ICatalogFilterForm {
  auditStatus?: SelectValue;
  status?: SelectValue;
  candidateType?: SelectValue;
  legalPersonName?: string;
  inn?: string;
  executiveFullName?: string;
  executiveEmail?: string;
  futureAddress?: string;
  executiveWorkPhone?: string;
  isCompetitive?: SelectValue;
}

const NOT_SELECTED_OPTION = { value: "", label: "Не выбран" };

const AUDIT_OPTIONS = [NOT_SELECTED_OPTION, ...getAuditStatusPopoverItems()];

const STATUS_OPTIONS = [NOT_SELECTED_OPTION, ...getStatusPopoverItems()];

const COMPETITIVE_OPTIONS = [
  { value: "", label: "Не выбран" },
  { value: "true", label: "Конкурсная" },
  { value: "false", label: "Внеконкурсная" },
];

const toSelect = (
  options: { value: string; label: string }[],
  raw: unknown,
): SelectValue => {
  const value = typeof raw === "string" ? raw : "";
  const found = options.find((o) => o.value === value);
  return found ?? options[0];
};

const formatPhone = (raw: string): string => {
  let digits = raw.replace(/\D/g, "");
  if (raw.startsWith("+7")) digits = digits.slice(1);
  if (digits.length > 10 && /^[78]/.test(digits)) digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (!digits) return "";

  let result = `+7 (${digits.slice(0, 3)}`;
  if (digits.length > 3) result += `) ${digits.slice(3, 6)}`;
  if (digits.length > 6) result += `-${digits.slice(6, 8)}`;
  if (digits.length > 8) result += `-${digits.slice(8, 10)}`;
  return result;
};

const CatalogFilters = () => {
  const { t } = useTranslation();
  const setParams = useURLParamsSetter();
  const url = useURLParamsGetter<Record<string, string>>();

  const candidateTypeOptions = getCandidateTypeOptions(t);

  const { control, handleSubmit, reset } = useForm<ICatalogFilterForm>({
    defaultValues: {
      auditStatus: toSelect(AUDIT_OPTIONS, url.auditStatus),
      status: toSelect(STATUS_OPTIONS, url.status),
      candidateType: toSelect(candidateTypeOptions, url.candidateType),
      legalPersonName: (url.legalPersonName as string) || "",
      inn: (url.inn as string) || "",
      executiveFullName: (url.executiveFullName as string) || "",
      executiveEmail: (url.executiveEmail as string) || "",
      futureAddress: (url.futureAddress as string) || "",
      executiveWorkPhone: formatPhone((url.executiveWorkPhone as string) || ""),
      isCompetitive: toSelect(COMPETITIVE_OPTIONS, url.isCompetitive),
    },
  });

  const onSubmit = (data: ICatalogFilterForm) => {
    setParams({
      auditStatus: data.auditStatus?.value || undefined,
      status: data.status?.value || undefined,
      candidateType: data.candidateType?.value || undefined,
      legalPersonName: data.legalPersonName || undefined,
      inn: data.inn || undefined,
      executiveFullName: data.executiveFullName || undefined,
      executiveEmail: data.executiveEmail || undefined,
      futureAddress: data.futureAddress || undefined,
      executiveWorkPhone: data.executiveWorkPhone || undefined,
      isCompetitive: data.isCompetitive?.value || undefined,
      distanceToNearestDealerFrom: undefined,
      distanceToNearestDealerTo: undefined,
      page: "0",
    });
  };

  const onReset = () => {
    reset({
      auditStatus: AUDIT_OPTIONS[0],
      status: STATUS_OPTIONS[0],
      candidateType: candidateTypeOptions[0],
      legalPersonName: "",
      inn: "",
      executiveFullName: "",
      executiveEmail: "",
      futureAddress: "",
      executiveWorkPhone: "",
      isCompetitive: COMPETITIVE_OPTIONS[0],
    });
    setParams({
      auditStatus: undefined,
      status: undefined,
      candidateType: undefined,
      legalPersonName: undefined,
      inn: undefined,
      executiveFullName: undefined,
      executiveEmail: undefined,
      futureAddress: undefined,
      executiveWorkPhone: undefined,
      isCompetitive: undefined,
      distanceToNearestDealerFrom: undefined,
      distanceToNearestDealerTo: undefined,
      page: "0",
    });
  };

  return (
    <form className={s.formFilter} onSubmit={handleSubmit(onSubmit)}>
      <div className={s.filterWrapper}>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("catalog.auditStatus")}</span>
          <Controller
            name="auditStatus"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelInValue
                value={value}
                onChange={onChange}
                options={AUDIT_OPTIONS}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("catalog.status")}</span>
          <Controller
            name="status"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelInValue
                value={value}
                onChange={onChange}
                options={STATUS_OPTIONS}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("catalog.candidateType")}</span>
          <Controller
            name="candidateType"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelInValue
                value={value}
                onChange={onChange}
                options={candidateTypeOptions}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("catalog.legalPersonName")}</span>
          <Controller
            name="legalPersonName"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("catalog.INN")}</span>
          <Controller
            name="inn"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("catalog.executiveFullName")}</span>
          <Controller
            name="executiveFullName"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
              />
            )}
          />
        </div>
      </div>

      <div className={s.filterWrapper}>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("catalog.executiveEmail")}</span>
          <Controller
            name="executiveEmail"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("catalog.futureAddress")}</span>
          <Controller
            name="futureAddress"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("catalog.executiveWorkPhone")}</span>
          <Controller
            name="executiveWorkPhone"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                placeholder="+7 (___) ___-__-__"
                onChange={(e) => onChange(formatPhone(e.target.value))}
                onBlur={onBlur}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("catalog.isCompetitive")}</span>
          <Controller
            name="isCompetitive"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelInValue
                value={value}
                onChange={onChange}
                options={COMPETITIVE_OPTIONS}
              />
            )}
          />
        </div>
      </div>

      <div className={s.buttons}>
        <Button
          htmlType="button"
          color="secondary"
          variant="text"
          bold={false}
          fontSize="12"
          size="medium"
          onClick={onReset}
        >
          {t("ui.filter_reset")}
        </Button>
        <Button
          htmlType="submit"
          bold
          color="secondary"
          fontSize="12"
          size="medium"
        >
          {t("ui.filter_show")}
        </Button>
      </div>
    </form>
  );
};

export default CatalogFilters;
